/**
 * The Library's one markdown surface (#278): `renderMarkdown` (the only sanitiser this app hands
 * to the DOM — see ./markdown.ts) plus the prose styles. The editor's preview, the document
 * reader and the concept guide all render through this, so markdown looks the same everywhere.
 */
import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { renderFieldMarkdown, renderMarkdown } from './markdown';

export const MARKDOWN_PROSE = cn(
  '[&_h1]:font-display [&_h1]:text-(length:--t-card) [&_h1]:font-semibold [&_h2]:font-display [&_h2]:text-(length:--t-section) [&_h2]:font-semibold',
  '[&_h3]:font-display [&_h3]:text-(length:--t-body) [&_h3]:font-semibold [&_p]:my-2 [&_p]:text-(length:--t-body) [&_p]:leading-relaxed',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:text-(length:--t-body)',
  '[&_a]:text-interactive [&_a]:underline [&_code]:font-mono [&_code]:text-(length:--t-meta)',
  '[&_pre]:my-2 [&_pre]:overflow-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3',
  '[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
  '[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_img]:max-w-full',
);

/**
 * `variant="field"` (#374) is a long-text field value in view mode: raw HTML in the value is
 * dropped rather than rendered (see `renderFieldMarkdown`). The default is a document body.
 */
export function MarkdownBody({
  source,
  className,
  variant = 'document',
  ...rest
}: {
  source: string;
  className?: string;
  variant?: 'document' | 'field';
  'data-testid'?: string;
  id?: string;
}) {
  const html = useMemo(() => (variant === 'field' ? renderFieldMarkdown(source) : renderMarkdown(source)), [source, variant]);
  return (
    <div
      {...rest}
      // R-13: rendered document prose — its markdown tables are content, not a kit DataTable.
      data-kit="prose"
      className={cn(MARKDOWN_PROSE, 'text-foreground', className)}
      // renderMarkdown is the only sanitiser this app hands to the DOM — see ./markdown.ts.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
